import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Schemas ────────────────────────────────────────────────────────────────

export const ItemCatalogItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  baseSku: z.string().nullable(),
  description: z.string().nullable(),
  isSellable: z.boolean(),
  isActive: z.boolean(),
  updatedAt: z.string(),
});

export const ListItemCatalogInputSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().optional(),
  categoryId: z.string().uuid().optional(),
  isSellable: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(["name", "baseSku"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export const ListItemCatalogResponseSchema = z.object({
  edges: z.array(ItemCatalogItemSchema),
  pageInfo: z.object({
    currentPage: z.number(),
    totalPages: z.number(),
    totalCount: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});

export const ListRexelItemsInputSchema = z.object({
  baseSku: z.string(),
});

export const InitinfcRecordSchema = z.object({
  id: z.string().uuid(),
  division: z.string(),
  fileVariant: z.string(),
  giitem: z.string(),
  giloc: z.string(),
  gidept: z.string().nullable(),
  gigroup: z.string().nullable(),
  gidstlvl: z.string().nullable(),
  gidescr: z.string().nullable(),
  giselprc: z.string().nullable(),
  gistdcst: z.string().nullable(),
  gifobcst: z.string().nullable(),
  giwght: z.string().nullable(),
  gicnvfct: z.string().nullable(),
  gimaxqty: z.string().nullable(),
  giminqty: z.string().nullable(),
  giincord: z.string().nullable(),
  gilead: z.string().nullable(),
  gisrvlvl: z.string().nullable(),
  gishlife: z.string().nullable(),
  gircvcst: z.string().nullable(),
  gicrycst: z.string().nullable(),
  giprcbrk: z.string().nullable(),
  gifrtbrk: z.string().nullable(),
  gimodel: z.string().nullable(),
  giinvcls: z.string().nullable(),
  gistkflg: z.string().nullable(),
  gionhand: z.number().int(),
  giunshpq: z.number().int(),
  gistrdat: z.string().nullable(),
  giparent1: z.string().nullable(),
  gipercent1: z.string().nullable(),
  giparent2: z.string().nullable(),
  gipercent2: z.string().nullable(),
  gimaxtyp: z.string().nullable(),
  gimaxval: z.string().nullable(),
  gissmin: z.string().nullable(),
  giuom: z.string().nullable(),
  gitype: z.string().nullable(),
  girsq: z.string().nullable(),
  girsqapl: z.string().nullable(),
  gilifetyp: z.string().nullable(),
  gibuyer: z.string().nullable(),
  gichar1: z.string().nullable(),
  gichar2: z.string().nullable(),
  gichar3: z.string().nullable(),
  gichar4: z.string().nullable(),
  ginum1: z.string().nullable(),
  ginum2: z.string().nullable(),
  ginum3: z.string().nullable(),
  ginum4: z.string().nullable(),
  gieor: z.string().nullable(),
});

export const ListRexelItemsResponseSchema = z.object({
  edges: z.array(InitinfcRecordSchema),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const ListPriceListLinesInputSchema = z.object({
  productId: z.string().uuid(),
});

export const PriceListLineItemSchema = z.object({
  id: z.string().uuid(),
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  unitPrice: z.string(),
  uomCode: z.string().nullable(),
  pricingMethod: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});

export const ListPriceListLinesResponseSchema = z.object({
  edges: z.array(PriceListLineItemSchema),
});

export const productsContracts = {
  listCatalog: oc
    .input(ListItemCatalogInputSchema)
    .output(ListItemCatalogResponseSchema)
    .route({ method: "GET" }),
  listRexelItems: oc
    .input(ListRexelItemsInputSchema)
    .output(ListRexelItemsResponseSchema)
    .route({ method: "GET" }),
  listPriceListLines: oc
    .input(ListPriceListLinesInputSchema)
    .output(ListPriceListLinesResponseSchema)
    .route({ method: "GET" }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ItemCatalogItem = z.infer<typeof ItemCatalogItemSchema>;
export type ListItemCatalogInput = z.infer<typeof ListItemCatalogInputSchema>;
export type ListItemCatalogResponse = z.infer<
  typeof ListItemCatalogResponseSchema
>;
export type InitinfcRecord = z.infer<typeof InitinfcRecordSchema>;
export type ListRexelItemsInput = z.infer<typeof ListRexelItemsInputSchema>;
export type ListRexelItemsResponse = z.infer<
  typeof ListRexelItemsResponseSchema
>;
export type PriceListLineItem = z.infer<typeof PriceListLineItemSchema>;
export type ListPriceListLinesInput = z.infer<
  typeof ListPriceListLinesInputSchema
>;
export type ListPriceListLinesResponse = z.infer<
  typeof ListPriceListLinesResponseSchema
>;
